import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import {
  NAVY, paint, foamMaterial, bake, hullGeometry, wakeGeometry, flagGeometry, waveFlag,
  sailor, HATS, shipWheel, Buoyancy
} from './units-style.js';

// A fast skiff that fits within 0.95 of the captain's 1.0 collision radius
const SKIFF = { stern: -0.76, bow: 0.88, beam: 0.95, top: 0.32, keel: -0.25, sheer: 0.16, transom: 0.7 };
const DECK_Y = 0.25;

// The hero: a captain in a team-coloured coat and tricorn hat at the wheel of
// a fast patrol skiff, with a brass swivel gun on the bow and the team flag astern
export class Player {
  constructor(scene, unit, color, isLocalPlayer = false, displayName = null) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isLocalPlayer = isLocalPlayer;
    this.displayName = displayName || `Player ${unit.ownerId + 1}`;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.respawnOverlay = null;
    this.nametagSprite = null;
    this.healthBar = null;
    this.maxHealth = 10; // PlayerUnitHealth from server
    this.isRespawning = null;
    this.geometries = [];
    this.materials = [];
    this.create();
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  part(group, parts) {
    const mesh = new THREE.Mesh(this.track(bake(parts)), this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  }

  create() {
    const bodyGroup = new THREE.Group();

    // Per-instance materials so respawn dimming only affects this captain
    this.material = paint({ unique: true });
    this.flagMaterial = new THREE.MeshStandardMaterial({ color: this.color, roughness: 0.9, flatShading: true, side: THREE.DoubleSide });
    for (const material of [this.material, this.flagMaterial]) {
      material.userData.baseOpacity = 1;
      this.materials.push(material);
    }

    // The boat floats; Buoyancy sets its height, roll and pitch
    this.model = new THREE.Group();
    bodyGroup.add(this.model);

    const team = this.color;
    this.part(this.model, [
      hullGeometry({ ...SKIFF, side: team, trim: NAVY.white }),
      // Thwart, wheel stand and a brass rail
      colored(new THREE.BoxGeometry(0.62, 0.06, 0.18), NAVY.woodDark, { y: DECK_Y + 0.12, z: -0.5 }),
      colored(new THREE.BoxGeometry(0.16, 0.6, 0.12), NAVY.woodDark, { y: DECK_Y + 0.3, z: 0.24 }),
      // The captain: team coat, white cuffs and cravat, tricorn hat
      ...sailor({ coat: team, hat: HATS.tricorn, scale: 1.45, y: DECK_Y, z: -0.12 }),
      colored(new THREE.BoxGeometry(0.16, 0.2, 0.06), NAVY.white, { y: DECK_Y + 1.15, z: 0.08 }),
      colored(new THREE.BoxGeometry(0.58, 0.06, 0.4), NAVY.gold, { y: DECK_Y + 1.33, z: -0.12 }),
      // Coat tails flaring behind
      colored(new THREE.BoxGeometry(0.5, 0.36, 0.08), team, { y: DECK_Y + 0.62, z: -0.33, rx: 0.25 }),
      // Swivel gun on a post at the bow
      colored(new THREE.CylinderGeometry(0.04, 0.05, 0.35, 5), NAVY.woodDark, { y: DECK_Y + 0.25, z: 0.56 }),
      colored(new THREE.CylinderGeometry(0.05, 0.07, 0.36, 6), NAVY.brass, { y: DECK_Y + 0.45, z: 0.64, rx: Math.PI / 2 }),
      // Flagstaff behind the captain
      colored(new THREE.CylinderGeometry(0.025, 0.03, 1.5, 5), NAVY.woodDark, { y: DECK_Y + 0.75, z: -0.32 })
    ]);

    // Ship's wheel turns as the skiff steers
    this.wheel = new THREE.Group();
    this.wheel.position.set(0, DECK_Y + 0.66, 0.31);
    this.wheel.rotation.x = -0.35;
    this.part(this.wheel, shipWheel(0.2, NAVY.woodLight, NAVY.brass));
    this.model.add(this.wheel);

    this.flag = new THREE.Mesh(this.track(flagGeometry(0.36, 0.26)), this.flagMaterial);
    this.flag.position.set(0, DECK_Y + 1.48, -0.32);
    this.flag.rotation.y = Math.PI / 2;
    this.model.add(this.flag);

    // Foam and the local-player ring sit flat on the water
    this.water = new THREE.Group();
    bodyGroup.add(this.water);
    this.wake = new THREE.Mesh(this.track(wakeGeometry({ ...SKIFF, spread: 0.08, tail: 0.1 })), foamMaterial());
    this.wake.renderOrder = 1;
    this.water.add(this.wake);

    // Indicator for local player
    if (this.isLocalPlayer) {
      const indicatorGeometry = new THREE.RingGeometry(1.5, 2, 32);
      const indicatorMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5
      });
      const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
      indicator.rotation.x = -Math.PI / 2;
      indicator.position.y = 0.1;
      this.water.add(indicator);
      this.indicator = indicator;
    }

    // Create floating nametag
    this.nametagSprite = this.createNametag();
    this.nametagSprite.position.y = 3.6;
    bodyGroup.add(this.nametagSprite);

    // Create health bar (between head and nametag)
    this.healthBar = new HealthBar(this.maxHealth, 2.5, 0.3);
    this.healthBar.getGroup().position.y = 2.95;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.buoyancy = new Buoyancy(this.mesh, this.model, { bob: 0.04, roll: 0.05, pitch: 0.06, topSpeed: 5 });
    this.buoyancy.update();

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate(rotationDiff);
    }
  }

  // Floating, foam, flag and a wheel that follows the helm
  animate(turning) {
    this.buoyancy.update();
    const speed = this.buoyancy.speed;
    this.wake.scale.set(1 + speed * 0.1, 1, 1);
    this.wake.visible = speed > 0.08 && !this.isRespawning;
    this.wheel.rotation.z += (Math.max(-1.5, Math.min(1.5, -turning * 3)) - this.wheel.rotation.z) * 0.1;
    waveFlag(this.flag, performance.now() / 1000 + this.buoyancy.phase, 0.08 + speed * 0.06);
  }

  // Set respawning state (dims the player)
  setRespawning(isRespawning) {
    if (!this.mesh || this.isRespawning === isRespawning) return;
    this.isRespawning = isRespawning;

    for (const material of this.materials) {
      material.transparent = isRespawning;
      material.opacity = material.userData.baseOpacity * (isRespawning ? 0.3 : 1);
      material.needsUpdate = true;
    }
  }

  // Aim at a target position
  aimAt(targetX, targetZ) {
    if (this.mesh) {
      const dx = targetX - this.mesh.position.x;
      const dz = targetZ - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  // Create a floating nametag sprite
  createNametag() {
    const name = this.displayName;

    // Create canvas for the nametag
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Get background color from player color
    const bgColor = typeof this.color === 'string' ? this.color : `#${this.color.toString(16).padStart(6, '0')}`;

    // Draw rounded rectangle background
    const padding = 10;
    const radius = 12;
    context.fillStyle = bgColor;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, radius);
    context.fill();

    // Add slight border
    context.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    context.lineWidth = 2;
    context.stroke();

    // Draw text
    context.fillStyle = '#ffffff';
    context.font = 'bold 28px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create sprite material
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false, // Always render on top
      depthWrite: false
    });

    // Create sprite
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(4, 1, 1); // Scale to match aspect ratio

    return sprite;
  }

  remove() {
    if (this.nametagSprite) {
      if (this.nametagSprite.material.map) {
        this.nametagSprite.material.map.dispose();
      }
      this.nametagSprite.material.dispose();
    }
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.indicator) {
      this.indicator.geometry.dispose();
      this.indicator.material.dispose();
    }
    this.geometries.forEach(g => g.dispose());
    this.materials.forEach(m => m.dispose());
    this.geometries = [];
    this.materials = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
